/**
 * Admin Dashboard — KPI cards.
 */
import { useEffect, useState } from "react";
import { api } from "@/App";
import { Users, Crown, FileQuestion, Activity, TrendingUp, Image as ImageIcon, Clock } from "lucide-react";

const KpiCard = ({ icon: Icon, label, value, sub, testid, tone = "primary" }) => {
  const toneClass = {
    primary: "bg-primary/15 text-primary",
    success: "bg-success/15 text-success",
    warning: "bg-warning/15 text-warning",
    accent: "bg-accent/15 text-accent",
  }[tone] || "bg-primary/15 text-primary";

  return (
    <div className="glass border border-border rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${toneClass}`}>
          <Icon className="w-5 h-5" />
        </div>
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
      <p className="text-3xl font-bold">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
};

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats")
      .then(setStats)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p data-testid="admin-dash-loading">Loading…</p>;
  if (!stats) return <p>Could not load stats.</p>;

  return (
    <div data-testid="admin-dashboard">
      <h2 className="text-2xl font-bold mb-1">Dashboard</h2>
      <p className="text-sm text-muted-foreground mb-6">Business and content KPIs at a glance.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard testid="kpi-total-users" icon={Users} label="Total users" value={stats.users.total} sub={`${stats.users.new_today} signed up today`} />
        <KpiCard testid="kpi-paid-pro" icon={Crown} label="Paid Pro" value={stats.users.paid_pro ?? 0} sub={`${stats.users.conversion_pct}% conversion (excl. free trials)`} tone="warning" />
        <KpiCard testid="kpi-trialing" icon={Clock} label="On 7-day trial" value={stats.users.trialing ?? 0} sub="Not paying yet — reverts to Free after 7 days" tone="accent" />
        <KpiCard testid="kpi-dau" icon={Activity} label="Active today" value={stats.activity.dau} sub={`${stats.activity.answers_today} MCQs answered`} tone="success" />
        <KpiCard testid="kpi-total-questions" icon={FileQuestion} label="Total MCQs" value={stats.content.total_questions} sub="Across all subjects" />
        <KpiCard testid="kpi-image-questions" icon={ImageIcon} label="Image MCQs" value={stats.content.image_questions} sub="Audit-verified" tone="accent" />
        <KpiCard testid="kpi-weekly-activity" icon={TrendingUp} label="Answers last 7 days" value={stats.activity.answers_week} sub="Engagement signal" />
      </div>

      <p className="text-xs text-muted-foreground mt-6">Last updated: {new Date(stats.generated_at).toLocaleString()}</p>
    </div>
  );
};

export default AdminDashboardPage;
